package com.flowyride.core.domain.speedcamera

import com.flowyride.core.domain.model.GeoPoint
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.hypot
import kotlin.math.max
import kotlin.math.min

/**
 * Cameras standing on [track]: within [radiusMeters] of one of its segments.
 * The radius absorbs the dataset's precision and the road width.
 */
fun List<SpeedCamera>.alongTrack(track: List<GeoPoint>, radiusMeters: Double = DEFAULT_RADIUS_METERS): List<SpeedCamera> =
    matchedAlongTrack(track, radiusMeters).map { it.camera }

/** @param segmentIndex index in the track of the first point of the segment the camera is closest to. */
data class CameraOnTrack(val camera: SpeedCamera, val segmentIndex: Int)

/**
 * Like [alongTrack], telling where each camera stands. In the cameras' order.
 *
 * Cameras are sorted into a grid and each segment only looks at the cells it crosses: comparing every
 * camera of the country with every segment of a 1 000 km ride takes minutes on a phone.
 */
fun List<SpeedCamera>.matchedAlongTrack(track: List<GeoPoint>, radiusMeters: Double = DEFAULT_RADIUS_METERS): List<CameraOnTrack> {
    if (track.size < 2 || isEmpty()) return emptyList()
    val cells = HashMap<Long, MutableList<Int>>()
    forEachIndexed { index, camera ->
        cells.getOrPut(cellKey(cellOf(camera.point.latitude), cellOf(camera.point.longitude))) { mutableListOf() } += index
    }

    val bestDistance = DoubleArray(size) { Double.MAX_VALUE }
    val bestSegment = IntArray(size) { -1 }
    val latitudeMargin = radiusMeters / METERS_PER_DEGREE_LATITUDE
    for (i in 0 until track.lastIndex) {
        val a = track[i]
        val b = track[i + 1]
        // Never narrower than the radius, even close to a pole.
        val longitudeMargin = latitudeMargin / max(0.01, cos(max(abs(a.latitude), abs(b.latitude)) * PI / 180))
        val firstRow = cellOf(min(a.latitude, b.latitude) - latitudeMargin)
        val lastRow = cellOf(max(a.latitude, b.latitude) + latitudeMargin)
        val firstColumn = cellOf(min(a.longitude, b.longitude) - longitudeMargin)
        val lastColumn = cellOf(max(a.longitude, b.longitude) + longitudeMargin)
        for (row in firstRow..lastRow) {
            for (column in firstColumn..lastColumn) {
                val candidates = cells[cellKey(row, column)] ?: continue
                for (index in candidates) {
                    val distance = distanceToSegmentMeters(this[index].point, a, b)
                    if (distance <= radiusMeters && distance < bestDistance[index]) {
                        bestDistance[index] = distance
                        bestSegment[index] = i
                    }
                }
            }
        }
    }
    return indices.filter { bestSegment[it] >= 0 }.map { CameraOnTrack(this[it], bestSegment[it]) }
}

private const val DEFAULT_RADIUS_METERS = 40.0
private const val METERS_PER_DEGREE_LATITUDE = 110_540.0

/** About 2 km: a road segment crosses one or two cells, a cell holds a handful of cameras at most. */
private const val CELL_DEGREES = 0.02

private fun cellOf(degrees: Double): Int = floor(degrees / CELL_DEGREES).toInt()

private fun cellKey(row: Int, column: Int): Long = row.toLong() shl 32 or (column.toLong() and 0xFFFFFFFFL)

/** Local flat projection: accurate at the scale of a road segment. */
private fun distanceToSegmentMeters(p: GeoPoint, a: GeoPoint, b: GeoPoint): Double {
    val metersPerDegreeLongitude = 111_320.0 * cos(p.latitude * PI / 180)
    val ax = (a.longitude - p.longitude) * metersPerDegreeLongitude
    val ay = (a.latitude - p.latitude) * METERS_PER_DEGREE_LATITUDE
    val bx = (b.longitude - p.longitude) * metersPerDegreeLongitude
    val by = (b.latitude - p.latitude) * METERS_PER_DEGREE_LATITUDE
    val dx = bx - ax
    val dy = by - ay
    val lengthSquared = dx * dx + dy * dy
    val t = if (lengthSquared == 0.0) 0.0 else (-(ax * dx + ay * dy) / lengthSquared).coerceIn(0.0, 1.0)
    return hypot(ax + t * dx, ay + t * dy)
}
